import { Fragment, useState } from 'react';
import {
  Center,
  Flex,
  Stack,
  Text,
  HStack,
  IconButton,
  Box,
  keyframes,
  VStack,
} from '@chakra-ui/react';
import { Tooltip } from 'react-tooltip';

const truncateText = (text: string, maxLength: number) =>
  text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

const fadeIn = keyframes`
  0% { opacity: 0;}
  100% { opacity: 1;}
`;

type CitationsButtonsProps = {
  buttonsData: {
    text: string;
    onClick: (text: string) => void;
    toolTip: {
      title: string;
      subTitle: string;
      body: string;
      citations?: string[];
      sourceIdx?: number;
    };
  }[];
};

export default function CitationsButtons({ buttonsData }: CitationsButtonsProps) {
  const [activeCitationIndices, setActiveCitationIndices] = useState<Record<string, number>>({});

  const handleNavigation = (
    e: React.MouseEvent,
    sourceIdx: number,
    direction: 'prev' | 'next',
    currentIndex: number,
    maxIndex: number
  ) => {
    e.stopPropagation();

    let newIndex = currentIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
    } else {
      newIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
    }

    setActiveCitationIndices((prev) => ({
      ...prev,
      [sourceIdx]: newIndex,
    }));
  };

  return (
    <>
      <Flex display="inline-flex" ml="5px" gap="4px" as="span">
        {buttonsData.map(({ text, onClick, toolTip }) => {
          const uniqueTooltipId = `${crypto?.randomUUID?.()?.slice(0, 12) || Math.random().toString(36).substring(2, 14)}${text}`;

          const hasCitationsNav =
            toolTip.citations && toolTip.citations.length > 1 && toolTip.sourceIdx !== undefined;
          const sourceIdx = toolTip.sourceIdx || 0;
          const currentCitationIndex = activeCitationIndices[sourceIdx] || 0;
          const citationsArray = toolTip.citations || [];
          const citationsCount = citationsArray.length;
          const currentCitation = citationsArray[currentCitationIndex];

          return (
            <Fragment key={`${text}-${sourceIdx}`}>
              <Tooltip
                id={uniqueTooltipId}
                place="bottom"
                noArrow
                opacity={1}
                wrapper={'span'}
                style={{
                  boxShadow: '0px 0px 10px 3px #0000001A',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  padding: '0px',
                  maxWidth: '80vw',
                }}
                clickable={true}
              >
                <Stack
                  color="black"
                  p={hasCitationsNav ? 0 : 1}
                  borderRadius="6px"
                  gap="0"
                  as="span"
                >
                  {hasCitationsNav && (
                    <HStack
                      justifyContent="space-between"
                      width="100%"
                      mb="1px"
                      roundedTop={'6px'}
                      color="white"
                      py={1}
                      px={2}
                      bgColor="greyNatural"
                    >
                      <HStack>
                        <IconButton
                          aria-label="Previous citation"
                          height="18px"
                          minWidth={'16px'}
                          icon={
                            <Box as="span" color="white" fontSize={'16px'}>
                              ←
                            </Box>
                          }
                          variant="plain"
                          onClick={(e) =>
                            handleNavigation(
                              e,
                              sourceIdx,
                              'prev',
                              currentCitationIndex,
                              citationsCount - 1
                            )
                          }
                        />
                        <IconButton
                          aria-label="Next citation"
                          height="16px"
                          minWidth={'16px'}
                          icon={
                            <Box as="span" color="white" fontSize={'16px'}>
                              →
                            </Box>
                          }
                          variant="plain"
                          onClick={(e) =>
                            handleNavigation(
                              e,
                              sourceIdx,
                              'next',
                              currentCitationIndex,
                              citationsCount - 1
                            )
                          }
                        />
                      </HStack>
                      <Text as="span" fontSize="14px">
                        {currentCitationIndex + 1}/{citationsCount}
                      </Text>
                    </HStack>
                  )}
                  <VStack gap="1" alignItems={'start'} px="3" py="1">
                    <Text as="span">{toolTip.title}</Text>
                    <Text fontSize="chatText" fontWeight="bold" as="span">
                      {truncateText(toolTip.subTitle, 70)}
                    </Text>

                    <Text as="span">{hasCitationsNav ? `"${currentCitation}"` : toolTip.body}</Text>
                  </VStack>
                </Stack>
              </Tooltip>

              <Center
                as="span"
                data-tooltip-id={uniqueTooltipId}
                boxSize="15px"
                border="1px"
                borderColor="greyNatural"
                _hover={{ bg: '#B8B8B8' }}
                bg="white"
                borderRadius="4px"
                cursor="pointer"
                fontSize="9px"
                fontWeight="bold"
                animation={`${fadeIn} 0.2s`}
                onClick={() => onClick(text)}
              >
                {text}
              </Center>
            </Fragment>
          );
        })}
      </Flex>
    </>
  );
}
